import { useState, type ComponentType } from 'react';
import { IconAlertTriangle, IconBuildingBank, IconCalendarOff, IconCheck, IconClockPlus, IconCreditCard, IconFileExport, IconFileSpreadsheet, IconFileText, IconMessageCircle, IconPackage, IconReceipt, IconReceiptRefund, IconReportMoney, IconRefresh, IconSearch, IconSettings, IconTrendingDown, IconUserPlus, IconUsersGroup, IconWorld, IconX, IconCashBanknote, IconFilterOff } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { APPROVALS, AP_KINDS, AP_TYPES, apNoBulk, filterApprovals, type ApRow } from './tasks';
import './i18n';
import s from './tasks.module.css';

// Mint-Tasks · tab "Duyệt chờ tôi" (code board: khối isApprTab). Số tiền phiếu ẩn (money=hide). Nút ghi báo toast.

type Ico = ComponentType<{ size?: number; stroke?: number; 'aria-hidden'?: boolean }>;
const ICON: Record<string, Ico> = {
  rel: IconFileExport, mg: IconTrendingDown, pay: IconCreditCard, do: IconPackage, lv: IconCalendarOff, ot: IconClockPlus, spl: IconUsersGroup, rop: IconRefresh,
  adm: IconSettings, svc: IconFileText, lc: IconFileText, hrd: IconUserPlus, prl: IconCashBanknote, portal: IconWorld, err: IconAlertTriangle, cost1: IconReportMoney,
  soa: IconFileSpreadsheet, rfd: IconReceiptRefund, bank: IconBuildingBank, iin: IconReceipt,
};

const stepColor = (st: string, i: number, cur: number, ok: boolean) => (ok && i === cur) || st === 'd' ? 'var(--ok)' : i === cur ? 'var(--d-blue)' : 'var(--line)';

export function ApprovalsPane({ state }: { state: ScreenState }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: t('pp.noWrite') });
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [types, setTypes] = useState<string[]>([]);
  const [pop, setPop] = useState(false);
  const [dec, setDec] = useState<Record<number, 'ok'>>({});
  const [pick, setPick] = useState<Record<number, true>>({});
  const rows = filterApprovals(APPROVALS, { q, kind, types });
  const pending = rows.filter((a) => !dec[a.id]);
  const picked = pending.filter((a) => pick[a.id]);
  const pickedOk = picked.filter((a) => !apNoBulk(a));
  const left = APPROVALS.filter((a) => !dec[a.id]).length;
  const toggle = (id: number) => setPick((p) => { const n = { ...p }; if (n[id]) delete n[id]; else n[id] = true; return n; });
  const approve = (a: ApRow) => { setDec((d) => ({ ...d, [a.id]: 'ok' })); toast({ msg: t('tapv.okToast', { k: a.kindLabel }) }); };
  const approveBulk = () => { if (!pickedOk.length) return; setDec((d) => { const n = { ...d }; pickedOk.forEach((a) => { n[a.id] = 'ok'; }); return n; }); setPick({}); toast({ msg: t('tapv.okBulkToast', { n: pickedOk.length }) }); };
  const typeLabel = types.length === 0 ? t('tapv.typeBtn') : types.length === 1 ? (AP_TYPES.find((x) => x[0] === types[0])?.[1] ?? '') + ' ▾' : t('tapv.typeN', { n: types.length });

  return (
    <div className={s.page}>
      <div className={s.bar}>
        <label className={s.search}>
          <IconSearch size={16} stroke={2.2} aria-hidden="true" />
          <input aria-label={t('tapv.searchLbl')} placeholder={t('tapv.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {AP_KINDS.map(([k, label]) => (
          <button key={k} type="button" className={s.ctl} aria-pressed={kind === k} onClick={() => { setKind(k); setPick({}); }}>
            {label} <span className={s.cnt}>{k === 'all' ? APPROVALS.filter((a) => !dec[a.id]).length : APPROVALS.filter((a) => a.grp === k && !dec[a.id]).length}</span>
          </button>
        ))}
        <div className={s.rel}>
          <button type="button" className={s.ctl} aria-haspopup="true" aria-expanded={pop} aria-pressed={types.length > 0} onClick={() => setPop((v) => !v)}>{typeLabel}</button>
          {pop && (
            <>
              <div className={s.scrim} onClick={() => setPop(false)} aria-hidden="true" />
              <div role="dialog" aria-label={t('tapv.filterLbl')} className={s.pop}>
                <div className={s.popHd}><b>{t('tapv.typeHd')}</b><span>{t('tapv.oneOf')}</span></div>
                <div className={s.popList}>
                  {AP_TYPES.map(([k, label]) => (
                    <label key={k} className={s.opt} data-on={types.includes(k)}>
                      <input type="checkbox" checked={types.includes(k)} onChange={() => { setTypes((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k])); setPick({}); }} />
                      <span>{label}</span><span className={s.cnt}>{APPROVALS.filter((a) => a.kind === k && !dec[a.id]).length}</span>
                    </label>
                  ))}
                </div>
                <div className={s.popFt}><span>{types.length ? t('tapv.filtering', { n: types.length }) : t('tapv.allTypes')}</span><button type="button" className={s.link} onClick={() => setTypes([])}><IconFilterOff size={16} aria-hidden="true" />{t('tapv.unpick')}</button></div>
              </div>
            </>
          )}
        </div>
        <div className={s.sums}>
          <span>{t('tapv.waitMe')} <b>{left}</b></span>
          <span>{t('tapv.oldest')} <b className={s.warnTx}>{t('tapv.days', { n: 3 })}</b></span>
        </div>
      </div>
      {picked.length > 0 && (
        <div role="region" aria-label={t('tapv.bulk')} className={s.bulk}>
          <b>{t('tapv.picked', { n: picked.length })}</b>
          <span>{picked.length > pickedOk.length ? t('tapv.flagged', { n: picked.length - pickedOk.length }) : t('tapv.nextSeat')}</span>
          <div>
            <button type="button" className={s.ghost} onClick={() => setPick({})}><IconFilterOff size={16} aria-hidden="true" />{t('tapv.unpick')}</button>
            <button type="button" className={s.outline} onClick={noWrite}>{t('tapv.rejectN', { n: picked.length })}</button>
            <button type="button" className={s.solid} disabled={pickedOk.length === 0} onClick={approveBulk}><IconCheck size={16} aria-hidden="true" />{t('tapv.approveN', { n: pickedOk.length })}</button>
          </div>
        </div>
      )}
      <StateView
        state={state}
        title={state === 'empty' ? t('tapv.emptyT') : undefined}
        action={state === 'error' ? <Button onClick={() => location.reload()}>{t('tasks.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={9} />}
      >
        <section className={s.grp} aria-label={t('tapv.listLbl')}>
          <div className={s.apHd}>
            <input type="checkbox" aria-label={t('tapv.pickAll')} checked={pending.length > 0 && picked.length === pending.length} onChange={() => setPick(picked.length === pending.length ? {} : Object.fromEntries(pending.map((a) => [a.id, true as const])))} />
            <span>{t('tapv.pendingHd', { n: pending.length, k: AP_TYPES.length })}</span>
            <span className={s.apHdR}>{t('tapv.seatHint')}</span>
          </div>
          {rows.map((a) => <ApRowView key={a.id} a={a} done={!!dec[a.id]} picked={!!pick[a.id] && !dec[a.id]} onPick={() => toggle(a.id)} onApprove={() => approve(a)} onWrite={noWrite} />)}
        </section>
        {rows.length === 0 && <div className={s.empty}>{t('tapv.emptyD')}</div>}
      </StateView>
    </div>
  );
}

function ApRowView({ a, done, picked, onPick, onApprove, onWrite }: { a: ApRow; done: boolean; picked: boolean; onPick: () => void; onApprove: () => void; onWrite: () => void }) {
  const t = useT();
  const Ic = ICON[a.kind] ?? IconFileText;
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  const seat = a.steps[a.cur]![1].split(' — ')[0] ?? '';
  const mee = a.kind === 'lv' || a.kind === 'ot';
  const navigate = useNavigate();
  return (
    <div className={`${s.apRow} ${done ? s.apDone : ''}`} onClick={() => void navigate({ to: '/ezy/tasks/$kind/$id', params: { kind: 'ap', id: String(a.id) } })}>
      <input type="checkbox" aria-label={t('tapv.pickOne', { a: a.title })} checked={picked} disabled={done} onClick={stop} onChange={onPick} />
      <span className={s.apIc}><Ic size={17} stroke={1.9} aria-hidden /></span>
      <div className={s.main}>
        <div className={s.apTitle}><b>{a.title}</b>{a.flags.map((f) => <span key={f.k} title={f.tip} className={`${s.chip} ${f.tone === 'warn' ? s.chipWarn : s.chipPri}`}>{f.k}</span>)}</div>
        <div>
          <a href="#" title={t('tapv.openSrc')} onClick={(e) => { e.preventDefault(); stop(e); }}>{a.kindLabel} ›</a>
          {mee && <> · <a href="#" title={t('tapv.sameReq')} onClick={(e) => { e.preventDefault(); stop(e); }}>{t('tapv.openMee')}</a></>}
          {' '}· {t('tapv.askedBy', { a: a.by })} · {a.when}
        </div>
      </div>
      <div className={s.apAmt}><div>{a.note}</div></div>
      <div className={s.apStep}>
        <div className={s.steps}>{a.steps.map(([k, who, st], i) => <div key={k} title={`${k} · ${who}`} style={{ background: stepColor(st, i, a.cur, done) }} />)}</div>
        <div>{t('tapv.step', { i: a.cur + 1, n: a.steps.length, seat })}</div>
      </div>
      <div className={s.apActs}>
        {done ? <span className={s.okTx}>{t('tapv.okDone')}</span> : (
          <>
            <button type="button" className={s.ghost} onClick={(e) => { stop(e); onWrite(); }}><IconMessageCircle size={16} aria-hidden="true" />{t('tapv.ask')}</button>
            <button type="button" className={s.outline} onClick={(e) => { stop(e); onWrite(); }}><IconX size={16} aria-hidden="true" />{t('tapv.reject')}</button>
            <button type="button" className={s.solid} title={t('tapv.approveStep', { i: a.cur + 1 })} onClick={(e) => { stop(e); onApprove(); }}><IconCheck size={16} aria-hidden="true" />{t('tapv.approve')}</button>
          </>
        )}
      </div>
    </div>
  );
}
